import React, { useMemo } from 'react';
import { FilterableMultiSelect } from '@carbon/react';
import { gql, useQuery } from '@apollo/client';

// Dummy students for fallback / development — will be replaced by fetched students
const DUMMY_STUDENTS = [
  { id: 's1', surname: 'Müller', lastname: 'Anna', username: 'anna.m' },
  { id: 's2', surname: 'Schmidt', lastname: 'Ben', username: 'ben.s' },
  { id: 's3', surname: 'Meier', lastname: 'Clara', username: 'clara.m' },
  { id: 's4', surname: 'Fischer', lastname: 'Daniel', username: 'daniel.f' },
  { id: 's5', surname: 'Weber', lastname: 'Eva', username: 'eva.w' },
];

// normalize for searching (handles umlauts/accents, case-insensitive)
const norm = (s = '') =>
  s
    .toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const GET_ALL_USERS = gql`
  query AllUsers($userTypes: [String!]!, $search: String) {
    allUsers(userTypes: $userTypes, search: $search) {
      id
      username
      surname
      lastname
    }
  }
`;

export default function MultiStudentSelector({ selectedStudents = [], onChange, search = null }) {
  // fetch all users of type STUDENT (server guaranteed to return only students)
  const { loading, error, data } = useQuery(GET_ALL_USERS, {
    variables: { userTypes: ['STUDENT'], search },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // build items from fetched students (or fallback to dummy)
  const sourceStudents = data && data.allUsers && data.allUsers.length > 0
    ? data.allUsers
    : DUMMY_STUDENTS;

  const items = useMemo(
    () =>
      sourceStudents.map((s) => ({
        id: s.id,
        // show lastname in uppercase like the single selector
        label: `${s.lastname ? s.lastname.toUpperCase() : ''} ${s.surname ? s.surname : ''}${s.username ? ` (${s.username})` : ''}`.trim(),
        // searchable text
        searchKey: `${s.lastname || ''} ${s.surname || ''} ${s.username || ''}`,
      })),
    [sourceStudents]
  );

  // map current selectedStudents (array of ids) to selected items objects
  const selectedItems = useMemo(() => items.filter((i) => selectedStudents.includes(i.id)), [items, selectedStudents]);

  const handleChange = ({ selectedItems }) => {
    const ids = selectedItems.map((i) => i.id);
    onChange?.(ids);
  };

  if (loading) return <p>Schüler werden geladen…</p>;
  if (error) return <p>Fehler beim Laden der Schüler: {error.message}</p>;
  if (!items || items.length === 0) return <p>Keine Schüler gefunden.</p>;

  return (
    <FilterableMultiSelect
      id="multi-student-selector"
      titleText="Schüler"
      placeholder="Suchen…"
      selectionFeedback="top"
      items={items}
      selectedItems={selectedItems}
      onChange={handleChange}
      itemToString={(item) => (item ? item.label : '')}
      shouldFilterItem={({ item, itemToString, inputValue }) => {
        const hay = norm(item.searchKey ?? itemToString(item));
        const needle = norm(inputValue);
        return hay.includes(needle);
      }}
    />
  );
}
